import { COMMENT_VIEW } from 'components/DocRightRail/Constants'
import { useAuthOrUnregisteredUser } from 'hooks/useAuthOrUnregisteredUser'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { forwardRef, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { CollabItemType } from 'stores/DocStore.types'
import { useRootStore } from 'stores/RootStore'
import { RootState } from 'types'
import { IReactNodeViewContext, useReactNodeView } from '../ReactNodeView/ReactNodeViewContext'
import Styles from './Card.module.scss'
import { ArticleCard } from './components/ArticleCard/ArticleCard'
import { UserCard } from './components/UserCard/UserCard'

interface Attrs {
  key?: string
  userId?: string
}

export type childCardProps = {
  nodeViewContext: IReactNodeViewContext
  isSelected: boolean
  isCommentSelected: boolean
  comments: Array<string>
  tasks: Array<string>
  currentWorkspaceId: number | undefined
  showMentionFormatBar: boolean
  setShowMentionFormatBar: (showMentionFormatBar: boolean) => void
}

export type userCardProps = childCardProps & {
  isUserSidebarOpen: boolean
  setIsUserSidebarOpen: (isUserSidebarOpen: boolean) => void
}

export const Card = observer(
  forwardRef<HTMLDivElement, {}>(() => {
    const [showMentionFormatBar, setShowMentionFormatBar] = useState<boolean>(false)
    const [isUserSidebarOpen, setIsUserSidebarOpen] = useState<boolean>(false)
    const { user: currentUser } = useAuthOrUnregisteredUser()
    const rootStore = useRootStore()
    const docStore = rootStore.docStore

    const context = useReactNodeView()
    const { view, node } = context
    const isSelected = context.isSelected ?? false
    const { key, userId } = node?.attrs as Attrs

    const filteredCommentsAndTasks = computed(() => (docStore ? docStore.orderedFilteredCollabItems : [])).get()
    const currentWorkspaceId = useSelector((state: RootState) => state.workspace.currentWorkspaceId)
    const [prevCommentId, setPrevCommentId] = useState<number | undefined>(undefined)
    const selectedCommentId = rootStore.comments.selected?.id
    const { isMobile } = useResponsiveDesign()
    const isCommentsRailVisible = computed(() =>
      Boolean(docStore?.rightRailStore.locked && docStore?.rightRailStore.activeTab === COMMENT_VIEW),
    ).get()

    useEffect(() => {
      // We don't want to interfere outside mobile or when the looking at the comments
      if (!isMobile && !isCommentsRailVisible) return
      // We only want to tag the comments as selected when the node is selected
      if (!isSelected) {
        setPrevCommentId(undefined)
        return
      }
      // Comments are stored under attrs.uuids, you can have multiple comments on the node
      const commentIds: number[] = node.attrs.uuids?.flatMap((uuid: string) => {
        if (!uuid) return []
        const comment = rootStore.comments.getByUuid(uuid)
        if (!comment) return []
        // We don't want to be opening resolved on click
        if (comment.resolved_at && !selectedCommentId) return []
        return comment.id
      })
      // If we don't even have comments, don't bother
      if (!view?.state.doc || !commentIds.length) return
      // If one of our comments is selected don't bother changing it
      if (selectedCommentId && commentIds.includes(selectedCommentId)) return
      // At this point we are selected but none of our comments is selected, so we go with the first
      const [commentId] = commentIds
      // If we are trying to unselect the comment while still selecting the component, don't interfere
      if (prevCommentId && prevCommentId === commentId) return
      // Every condition was met
      setPrevCommentId(commentId)
      rootStore.comments.select(commentId)
    }, [isSelected, node, view, rootStore, selectedCommentId, isMobile, isCommentsRailVisible, prevCommentId])

    useEffect(() => {
      setShowMentionFormatBar(isSelected)
      if (!isSelected) setIsUserSidebarOpen(false)
    }, [isSelected])

    const tasks: Array<string> = useMemo(() => {
      if (!currentUser || !node || !node.attrs.taskUuids || node.attrs.taskUuids.length === 0) return []

      const allTasksOnCard = node.attrs.taskUuids
      let filteredTasks: string[] = []

      for (const collabItem of filteredCommentsAndTasks.values()) {
        if (
          collabItem.type === CollabItemType.Todo &&
          typeof collabItem.item.uuid === 'string' &&
          allTasksOnCard.includes(collabItem.item.uuid)
        )
          filteredTasks.push(collabItem.item.uuid)
      }

      return filteredTasks
    }, [node, currentUser, filteredCommentsAndTasks])

    const comments: Array<string> = useMemo(() => {
      if (!node || !node.attrs.uuids || node.attrs.uuids.length === 0) return []

      const allCommentsOnCard = node.attrs.uuids
      let filteredComments: string[] = []

      for (const collabItem of filteredCommentsAndTasks.values()) {
        if (
          collabItem.type === CollabItemType.Comment &&
          typeof collabItem.item.uuid === 'string' &&
          allCommentsOnCard.includes(collabItem.item.uuid)
        )
          filteredComments.push(collabItem.item.uuid)
      }

      return filteredComments
    }, [node, filteredCommentsAndTasks])

    const isCommentSelected = computed(() => {
      return (isMobile || isCommentsRailVisible) && comments.some(rootStore.comments.isUuidSelected)
    }).get()

    if ((!key && !userId) || !view || !node || !context.getPos) return null

    return (
      <div
        data-test-id="cardComponent"
        data-uuid={comments.concat(tasks).join(' ')}
        className={Styles.outerCardWrapper}
      >
        {node.attrs.mentionType === 'User' && (
          <UserCard
            nodeViewContext={context as IReactNodeViewContext}
            isSelected={isSelected}
            isCommentSelected={!isSelected && isCommentSelected}
            comments={comments}
            tasks={tasks}
            currentWorkspaceId={currentWorkspaceId}
            showMentionFormatBar={showMentionFormatBar}
            setShowMentionFormatBar={setShowMentionFormatBar}
            isUserSidebarOpen={isUserSidebarOpen}
            setIsUserSidebarOpen={setIsUserSidebarOpen}
          />
        )}
        {node.attrs.mentionType === 'Article' && (
          <ArticleCard
            nodeViewContext={context as IReactNodeViewContext}
            isSelected={isSelected}
            isCommentSelected={!isSelected && isCommentSelected}
            comments={comments}
            tasks={tasks}
            currentWorkspaceId={currentWorkspaceId}
            showMentionFormatBar={showMentionFormatBar}
            setShowMentionFormatBar={setShowMentionFormatBar}
          />
        )}
      </div>
    )
  }),
)
